import "core-js/stable";
import "regenerator-runtime/runtime";
import type {
  RanWorkflow,
  StartNewWorkflowReq,
  WorkflowVersion,
  WorkflowVersionFilter,
  RanWorkflowFilter,
  RecordingDetails,
  Process,
  ProcessInstanceFilterResponse,
  PaginationBarDetails,
  Recording,
  WorkflowFilter,
  User,
  UserForm,
  Company,
  ClankPermissions,
  RecordingOsPermissionKind,
  AnalyzerStage,
  RecordingsGlobalStats,
  AdminRecordingWriteResult,
  VocabularyEntryForm,
  CategoryForm,
  CategoryUpdateForm,
  PromptSnippetForm,
  UserEnrollEmailForm,
} from "./types";
import type { PaginatedResponse } from "./auto-types/PaginatedResponse";

import { getWsUrl } from "./low_level";
import {
  coreClient,
  createCompanyRouterClient,
  expectData,
  expectOk,
  get_all_paginated_pages,
  get_company_api_url,
} from "./openapi_clients";
import type { RecordingSettings, ScreenshotRedactionSettings } from "./openapi-aliases";
import type { components as CoreComponents } from "./openapi/core";
import type { components as CompanyRouterComponents } from "./openapi/company-router";
import type { Assert } from "./typechecking";
import {
  IS_COMPANY_ROUTER_FRONTEND,
  PUBLIC_VERSION,
  IS_STATIC_DEMO_FRONTEND,
  frontendCapabilities,
} from "../globals";
export { getWsUrl } from "./low_level";
export type { User } from "./types";

type OpenApiDocumentWithVersion = {
  info?: {
    version?: unknown;
  };
};

export type RuntimeApiVersions = {
  core: string;
  companyRouter: string;
};

export const default_pagination_props: PaginationBarDetails = {
  page: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 50,
};

export { get_company_api_url } from "./openapi_clients";
export {
  apiErrorEnvelope,
  companyRouterFetch,
  formatApiRequestError,
  formatPublicMessage,
  isApiRequestError,
  isClankApiErrorEnvelope,
  splitApiRequestFormErrors,
} from "./openapi_clients";
export type {
  ClankApiErrorEnvelope,
  ClankPublicMessage,
  ClankResourceLookup,
} from "./openapi_clients";

export type UserWhoami = CompanyRouterComponents["schemas"]["WhoamiResponse"];
export type WorkflowListItem = CoreComponents["schemas"]["WorkflowListItem"];
export type RecordingScreenshotFormat = CoreComponents["schemas"]["RecordingScreenshotFormat"];
export type ScreenshotFormatPayload = CoreComponents["schemas"]["ScreenshotFormatPayload"];
export type AutoAnalyzePayload = CoreComponents["schemas"]["AutoAnalyzePayload"];
export type UserUpdateForm = CompanyRouterComponents["schemas"]["UserUpdateForm"];
export type CreateCompanyForm = CompanyRouterComponents["schemas"]["CreateCompanyForm"];
export type CompanyUpdateForm = CompanyRouterComponents["schemas"]["CompanyUpdateForm"];
export type OwnPasswordUpdateForm = CompanyRouterComponents["schemas"]["OwnPasswordUpdateForm"];
/**
 * What a chat is about. Its own schema now rather than the request minus the
 * transcript, because a user message carries one too: each turn records the
 * scope it was asked under, so a mid-conversation change is appended to the
 * transcript instead of rewriting the (cached) system context.
 */
export type ChatScopeFilters = CompanyRouterComponents["schemas"]["ChatScopeFilters"];

export type ChatWsEvent = CompanyRouterComponents["schemas"]["ChatWsEvent"];
export type NotificationSettings = CompanyRouterComponents["schemas"]["NotificationSettingsForm"];
export type NotificationFrequency = CompanyRouterComponents["schemas"]["NotificationFrequency"];
export type NotificationChannel = CompanyRouterComponents["schemas"]["NotificationChannel"];
export type MfaStatus = CompanyRouterComponents["schemas"]["MfaStatus"];
export type MfaEnrollStartResponse = CompanyRouterComponents["schemas"]["MfaEnrollStartResponse"];
export type MfaEnrollConfirmForm = CompanyRouterComponents["schemas"]["MfaEnrollConfirmForm"];
export type MfaTotpCodeForm = CompanyRouterComponents["schemas"]["MfaTotpCodeForm"];
export type ChatThinkingKind = CompanyRouterComponents["schemas"]["ChatThinkingKind"];
export type AdminCreateRecordingRequest =
  CompanyRouterComponents["schemas"]["AdminCreateRecordingRequest"];
export type AdminPatchRecordingRequest =
  CompanyRouterComponents["schemas"]["AdminPatchRecordingRequest"];
export type SeededDeletionResponse = CompanyRouterComponents["schemas"]["SeededDeletionResponse"];

export const LOGOUT_ROUTE = IS_COMPANY_ROUTER_FRONTEND
  ? `/api/logout/?redirect=${window.location.origin}/`
  : `/login/logout/`;

export const get_chat_ws_url = (): string => getWsUrl("/ws/clank/chat/", get_company_api_url());

const readOpenApiVersion = async (url: string): Promise<string> => {
  const response = await fetch(url, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Failed to load OpenAPI document ${url}: ${response.status}`);
  }

  const body = (await response.json()) as OpenApiDocumentWithVersion;
  if (typeof body.info?.version !== "string" || body.info.version.trim() === "") {
    throw new Error(`OpenAPI document ${url} does not contain info.version`);
  }

  return body.info.version;
};

export const get_runtime_api_versions = async (): Promise<RuntimeApiVersions> => {
  const companyRouterBaseUrl = get_company_api_url();
  const [core, companyRouter] = await Promise.all([
    readOpenApiVersion("/api/openapi.json"),
    readOpenApiVersion(`${companyRouterBaseUrl}/api/openapi.json`),
  ]);

  return {
    core,
    companyRouter,
  };
};

export const get_whoami = async () => {
  if (IS_COMPANY_ROUTER_FRONTEND) {
    const companyClient = createCompanyRouterClient();
    return expectData(await companyClient.GET("/api/whoami/"), "Failed to load current user");
  }

  return expectData(await coreClient.GET("/api/whoami/"), "Failed to load current user");
};
export const get_workflows = async (page: number, options?: { page_size?: number }) =>
  expectData(
    await coreClient.GET("/api/workflows/", {
      params: {
        query: {
          page,
          page_size: options?.page_size,
        },
      },
    }),
    "Failed to load workflows",
  );
export const get_all_workflows = async (
  filter?: Partial<WorkflowFilter> & { _updated_at?: string | null },
) =>
  get_all_paginated_pages<WorkflowListItem>(async page =>
    expectData(
      await coreClient.GET("/api/workflows/", {
        params: {
          query: {
            page,
            _updated_at: filter?._updated_at ?? undefined,
            page_size: filter?.page_size,
          },
        },
      }),
      "Failed to load workflows",
    ),
  );
export const start_new_workflow = async (req: StartNewWorkflowReq) =>
  expectData(
    await coreClient.POST("/api/workflow/run/", { body: req }),
    "Failed to start workflow",
  );
export const get_ran_workflows = async (page: number, filter: RanWorkflowFilter) =>
  expectData(
    await coreClient.GET("/api/ran-workflows/", {
      params: {
        query: {
          ...filter,
          page,
        },
      },
    }),
    "Failed to load ran workflows",
  );
export const get_all_ran_workflows = async (
  filter: Partial<RanWorkflowFilter> & { _updated_at?: string | null },
) =>
  get_all_paginated_pages<RanWorkflow>(async page =>
    expectData(
      await coreClient.GET("/api/ran-workflows/", {
        params: {
          query: {
            ...filter,
            _updated_at: filter._updated_at ?? undefined,
            page,
          },
        },
      }),
      "Failed to load ran workflows",
    ),
  );

export const get_ran_workflow = async (id: string) =>
  expectData(
    await coreClient.GET("/api/ran-workflows/{ran_workflow_id}/", {
      params: {
        path: { ran_workflow_id: id },
      },
    }),
    "Failed to get ran workflows",
  );

export const get_workflow_version = async (
  page: number,
  filter: Omit<WorkflowVersionFilter, "page" | "page_size">,
) =>
  expectData(
    await coreClient.GET("/api/workflow-versions/", {
      params: {
        query: {
          ...filter,
          page,
        },
      },
    }),
    "Failed to load workflow versions",
  );
export const get_all_workflow_version = async (
  filter: Partial<WorkflowVersionFilter> & { _updated_at?: string | null },
) =>
  get_all_paginated_pages<WorkflowVersion>(async page =>
    expectData(
      await coreClient.GET("/api/workflow-versions/", {
        params: {
          query: {
            ...filter,
            _updated_at: filter._updated_at ?? undefined,
            page,
          },
        },
      }),
      "Failed to load workflow versions",
    ),
  );

const get_recordings_list_page = async (
  page: number,
  options?: {
    page_size?: number;
    user_id_list?: string;
    datetime_gte?: string;
    datetime_lte?: string;
    include_in_processes?: boolean;
  },
): Promise<PaginatedResponse<Recording, RecordingsGlobalStats | null>> => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/recordings/", {
      params: {
        query: {
          page,
          ...options,
        },
      },
    }),
    "Failed to load recordings",
  );
};

export type RecordingsWithStats = {
  recordings: Recording[];
  stats: RecordingsGlobalStats | null;
};

/** Absent `user_ids` loads everyone's recordings; an empty list loads nobody's. */
export const get_all_recordings = async (options?: {
  user_ids?: string[];
  datetime_gte?: string;
  datetime_lte?: string;
  include_in_processes?: boolean;
}): Promise<RecordingsWithStats> => {
  const pages = await get_all_paginated_pages<Recording, RecordingsGlobalStats | null>(page =>
    get_recordings_list_page(page, {
      page_size: 100,
      user_id_list: options?.user_ids?.join(","),
      datetime_gte: options?.datetime_gte,
      datetime_lte: options?.datetime_lte,
      include_in_processes: options?.include_in_processes,
    }),
  );
  return { recordings: pages.items, stats: pages.extra };
};

export const get_config = async () => {
  if (IS_COMPANY_ROUTER_FRONTEND) {
    return {
      company_router_url: "",
      version: PUBLIC_VERSION,
    };
  }

  return expectData(await coreClient.GET("/api/config/"), "Failed to load config");
};

/**
 * One recording with its analysis. `runId` picks a run out of its history;
 * without it the newest completed run is returned, which is all a user without
 * the run history can see.
 */
export const get_recording_details = async (
  recordingId: string,
  runId?: string,
): Promise<RecordingDetails> => {
  const companyClient = createCompanyRouterClient();
  const result = await companyClient.GET("/api/clank/recordings/{recording_id}/", {
    params: {
      path: {
        recording_id: recordingId,
      },
      query: runId ? { run: runId } : {},
    },
  });
  return expectData(result, "Failed to load recording details");
};

/** The recording's analysis runs, newest first. Admin-only. */
export const list_recording_analysis_runs = async (recordingId: string) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/recordings/{recording_id}/analyses/", {
      params: {
        path: {
          recording_id: recordingId,
        },
      },
    }),
    "Failed to load analysis runs",
  );
};

/** Label one analysis run, or clear its label with `null`. Admin-only. */
export const rename_recording_analysis_run = async (
  recordingId: string,
  runId: string,
  name: string | null,
) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.PATCH("/api/clank/recordings/{recording_id}/analyses/{run_id}/", {
      params: {
        path: {
          recording_id: recordingId,
          run_id: runId,
        },
      },
      body: { name },
    }),
    "Failed to rename analysis run",
  );
};

/** Delete one analysis run, keeping the recording and its other runs. Admin-only. */
export const delete_recording_analysis_run = async (recordingId: string, runId: string) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.DELETE("/api/clank/recordings/{recording_id}/analyses/{run_id}/", {
      params: {
        path: {
          recording_id: recordingId,
          run_id: runId,
        },
      },
    }),
    "Failed to delete analysis run",
  );
};

/**
 * The stored analysis — the analytics plus the diagnostics report — or `null`
 * when the recording has no stored analysis. Admin-only.
 */
export const admin_get_recording_analysis_blob = async (recordingId: string) => {
  const companyClient = createCompanyRouterClient();
  const result = await companyClient.GET("/api/clank/admin/recordings/{recording_id}/", {
    params: {
      path: {
        recording_id: recordingId,
      },
    },
  });
  return expectData(result, "Failed to load the stored analysis");
};

export const admin_create_recording = async (
  body: AdminCreateRecordingRequest,
): Promise<AdminRecordingWriteResult> => {
  const companyClient = createCompanyRouterClient();
  const result = await companyClient.POST("/api/clank/admin/recordings/", {
    body,
  });
  return expectData(result, "Failed to import recording analysis");
};

/** Ids of every user the demo importer created — the only ones the seeded deletes accept. */
export const admin_list_seeded_user_ids = async (): Promise<string[]> => {
  const companyClient = createCompanyRouterClient();
  const result = await companyClient.GET("/api/clank/admin/seeded-users/");
  return expectData(result, "Failed to load seeded users");
};

/** Removes a company's seeded users, or just one of them when `userId` is given. */
export const admin_delete_seeded = async (
  companyId: number,
  userId?: string,
): Promise<SeededDeletionResponse> => {
  const companyClient = createCompanyRouterClient();
  const result = await companyClient.DELETE("/api/clank/admin/seeded-users/{company_id}/", {
    params: { path: { company_id: companyId }, query: { user_id: userId } },
  });
  return expectData(result, "Failed to delete demo users");
};

export const admin_patch_recording = async (
  recordingId: string,
  body: AdminPatchRecordingRequest,
): Promise<AdminRecordingWriteResult> => {
  const companyClient = createCompanyRouterClient();
  const result = await companyClient.PATCH("/api/clank/admin/recordings/{recording_id}/", {
    params: {
      path: {
        recording_id: recordingId,
      },
    },
    body,
  });
  return expectData(result, "Failed to update recording analysis");
};

/** Admin-only pipeline diagnostics for one analyzed recording. */
export const get_recording_analysis_report = async (recordingId: string, runId?: string) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/recordings/{recording_id}/report/", {
      params: {
        path: {
          recording_id: recordingId,
        },
        query: runId ? { run: runId } : {},
      },
    }),
    "Failed to load analysis report",
  );
};

export const delete_recording = async (recordingId: string) => {
  const companyClient = createCompanyRouterClient();
  return expectOk(
    await companyClient.DELETE("/api/clank/recordings/{recording_id}/", {
      params: {
        path: {
          recording_id: recordingId,
        },
      },
    }),
    "Failed to delete recording",
  );
};

export const get_recording_overview = async (query: {
  user_id_list?: string;
  datetime_gte?: string;
  datetime_lte?: string;
}) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/recordings/overview/", { params: { query } }),
    "Failed to load recording overview",
  );
};

export const get_recording_summaries = async (recordingIds: string[]) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/recordings/summaries/", {
      params: { query: { recording_id_list: recordingIds.join(",") } },
    }),
    "Failed to load recording summaries",
  );
};

/** Absent `user_ids` lists everyone's processes; an empty list lists nobody's. */
export const get_processes_list = async (
  options?: {
    user_ids?: string[];
    datetime_gte?: string;
    datetime_lte?: string;
  },
  signal?: AbortSignal,
) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/processes/", {
      params: {
        query: {
          datetime_gte: options?.datetime_gte || undefined,
          datetime_lte: options?.datetime_lte || undefined,
          user_id_list: options?.user_ids?.join(","),
        },
      },
      signal,
    }),
    "Failed to load process summaries",
  );
};

export const get_process_details = async (slug: string, signal?: AbortSignal): Promise<Process> => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/processes/{slug}/", {
      params: {
        path: {
          slug,
        },
      },
      signal,
    }),
    "Failed to load process details",
  );
};

export const filter_process_instances = async (
  slug: string,
  query: string,
  userIdList?: string,
): Promise<ProcessInstanceFilterResponse> => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.POST("/api/clank/processes/{slug}/filter/", {
      params: {
        path: {
          slug,
        },
      },
      body: { query, userIdList },
    }),
    "Failed to filter process instances",
  );
};

export const get_vocabulary_catalogue = async () => {
  const companyClient = createCompanyRouterClient();
  return expectData(await companyClient.GET("/api/clank/vocabulary/"), "Failed to load vocabulary");
};

export const get_vocabulary_entries = async () => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/vocabulary/entries/"),
    "Failed to load vocabulary entries",
  );
};

export const create_vocabulary_entry = async (body: VocabularyEntryForm) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.POST("/api/clank/vocabulary/entries/", { body }),
    "Failed to add vocabulary entry",
  );
};

export const update_vocabulary_entry = async (entryId: string, body: VocabularyEntryForm) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.PUT("/api/clank/vocabulary/entries/{entry_id}/", {
      params: {
        path: {
          entry_id: entryId,
        },
      },
      body,
    }),
    "Failed to update vocabulary entry",
  );
};

export const delete_vocabulary_entry = async (entryId: string) => {
  const companyClient = createCompanyRouterClient();
  return expectOk(
    await companyClient.DELETE("/api/clank/vocabulary/entries/{entry_id}/", {
      params: {
        path: {
          entry_id: entryId,
        },
      },
    }),
    "Failed to delete vocabulary entry",
  );
};

export const get_categories = async () => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/vocabulary/categories/"),
    "Failed to load categories",
  );
};

export const create_category = async (body: CategoryForm) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.POST("/api/clank/vocabulary/categories/", { body }),
    "Failed to add category",
  );
};

export const update_category = async (categoryId: string, body: CategoryUpdateForm) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.PUT("/api/clank/vocabulary/categories/{category_id}/", {
      params: {
        path: {
          category_id: categoryId,
        },
      },
      body,
    }),
    "Failed to update category",
  );
};

export const get_prompt_slots = async (companyId: number | null) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/prompt-slots/", {
      params: {
        query: {
          company_id: companyId ?? undefined,
        },
      },
    }),
    "Failed to load prompt configuration",
  );
};

export const get_prompt_snippets = async () => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/prompt-snippets/"),
    "Failed to load prompt snippets",
  );
};

export const create_prompt_snippet = async (body: PromptSnippetForm) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.POST("/api/clank/prompt-snippets/", { body }),
    "Failed to store prompt snippet",
  );
};

export const delete_prompt_snippet = async (snippetId: string) => {
  const companyClient = createCompanyRouterClient();
  return expectOk(
    await companyClient.DELETE("/api/clank/prompt-snippets/{snippet_id}/", {
      params: {
        path: {
          snippet_id: snippetId,
        },
      },
    }),
    "Failed to delete prompt snippet",
  );
};

export const assign_prompt_slot = async (
  prompt: string,
  slot: string,
  snippetId: string,
  companyId: number | null,
) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.PUT("/api/clank/prompt-slots/{prompt}/{slot}/", {
      params: {
        path: {
          prompt,
          slot,
        },
        query: {
          company_id: companyId ?? undefined,
        },
      },
      body: { snippet_id: snippetId },
    }),
    "Failed to assign prompt slot",
  );
};

export const clear_prompt_slot = async (prompt: string, slot: string, companyId: number | null) => {
  const companyClient = createCompanyRouterClient();
  return expectOk(
    await companyClient.DELETE("/api/clank/prompt-slots/{prompt}/{slot}/", {
      params: {
        path: {
          prompt,
          slot,
        },
        query: {
          company_id: companyId ?? undefined,
        },
      },
    }),
    "Failed to reset prompt slot",
  );
};

const runRecordingCommand = async (cmd: string) =>
  expectData(
    await coreClient.POST("/api/recording/{cmd}/", {
      params: {
        path: {
          cmd,
        },
      },
    }),
    `Failed to ${cmd} recording`,
  );

export const start_recording = async () => runRecordingCommand("start");
export const pause_recording = async () => runRecordingCommand("pause");
export const resume_recording = async () => runRecordingCommand("resume");
export const stop_recording = async () => runRecordingCommand("stop");
export const sync_recording = async () =>
  expectData(await coreClient.POST("/api/recording/sync/"), "Failed to sync recording");
export const get_review_recordings = async () =>
  expectData(
    await coreClient.GET("/api/recording/review/"),
    "Failed to load recordings awaiting review",
  );
export const get_review_screenshots = async (recordingId: string) =>
  expectData(
    await coreClient.GET("/api/recording/review/{recording}/screenshots/", {
      params: { path: { recording: recordingId } },
    }),
    "Failed to load review screenshots",
  );
/** Where a screenshot of a recording awaiting review is served from; the
 * image is loaded straight from this URL rather than through the client. */
export const review_screenshot_url = (recordingId: string, filename: string) =>
  `/api/recording/review/${encodeURIComponent(recordingId)}/screenshots/${encodeURIComponent(filename)}`;
export const approve_review_recording = async (
  recordingId: string,
  excludedScreenshots: string[],
) =>
  expectData(
    await coreClient.POST("/api/recording/review/{recording}/approve/", {
      params: { path: { recording: recordingId } },
      body: { excluded_screenshots: excludedScreenshots },
    }),
    "Failed to approve recording",
  );
export const delete_review_recording = async (recordingId: string) =>
  expectData(
    await coreClient.DELETE("/api/recording/review/{recording}/", {
      params: { path: { recording: recordingId } },
    }),
    "Failed to delete recording awaiting review",
  );
export const get_recording_screenshot_format = async () =>
  expectData(
    await coreClient.GET("/api/recording/screenshot-format/"),
    "Failed to load recording screenshot format",
  );
export const put_recording_screenshot_format = async (
  screenshotFormat: RecordingScreenshotFormat,
) =>
  expectData(
    await coreClient.PUT("/api/recording/screenshot-format/", {
      body: {
        screenshot_format: screenshotFormat,
      },
    }),
    "Failed to update recording screenshot format",
  );
export const get_recording_screenshot_blacklist = async () =>
  expectData(
    await coreClient.GET("/api/recording/screenshot-blacklist/"),
    "Failed to load screenshot blacklist",
  );
export const add_recording_screenshot_blacklist_apps = async (apps: string[]) =>
  expectData(
    await coreClient.POST("/api/recording/screenshot-blacklist/", {
      body: { apps },
    }),
    "Failed to add apps to screenshot blacklist",
  );
export const remove_recording_screenshot_blacklist_apps = async (apps: string[]) =>
  expectData(
    await coreClient.DELETE("/api/recording/screenshot-blacklist/", {
      body: { apps },
    }),
    "Failed to remove apps from screenshot blacklist",
  );
export const get_recording_screenshot_redaction = async () =>
  expectData(
    await coreClient.GET("/api/recording/screenshot-redaction/"),
    "Failed to load screenshot redaction setting",
  );
export const put_recording_screenshot_redaction = async (settings: ScreenshotRedactionSettings) =>
  expectData(
    await coreClient.PUT("/api/recording/screenshot-redaction/", {
      body: settings,
    }),
    "Failed to update screenshot redaction setting",
  );
export const get_recording_settings = async () =>
  expectData(await coreClient.GET("/api/recording/settings/"), "Failed to load recording settings");
export const put_recording_settings = async (settings: RecordingSettings) =>
  expectData(
    await coreClient.PUT("/api/recording/settings/", {
      body: settings,
    }),
    "Failed to update recording settings",
  );
export const get_recording_auto_analyze = async (): Promise<AutoAnalyzePayload> =>
  expectData(
    await coreClient.GET("/api/recording/auto-analyze/"),
    "Failed to load recording auto-analysis setting",
  );
export const put_recording_auto_analyze = async (
  autoAnalyzeAfterSync: boolean,
): Promise<AutoAnalyzePayload> =>
  expectData(
    await coreClient.PUT("/api/recording/auto-analyze/", {
      body: {
        auto_analyze_after_sync: autoAnalyzeAfterSync,
      },
    }),
    "Failed to update recording auto-analysis setting",
  );
export const get_notification_settings = async (): Promise<NotificationSettings> => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/notification-settings/", {}),
    "Failed to load notification settings",
  );
};
export const put_notification_settings = async (
  settings: NotificationSettings,
): Promise<NotificationSettings> => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.PUT("/api/clank/notification-settings/", {
      body: settings,
    }),
    "Failed to update notification settings",
  );
};
export const change_own_password = async (body: OwnPasswordUpdateForm): Promise<void> => {
  const companyClient = createCompanyRouterClient();
  return expectOk(
    await companyClient.PUT("/api/clank/user/password/", { body }),
    "Failed to change password",
  );
};

export const get_mfa_status = async (): Promise<MfaStatus> => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/user/mfa/"),
    "Failed to load two-factor status",
  );
};
export const start_mfa_enrollment = async (): Promise<MfaEnrollStartResponse> => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.POST("/api/clank/user/mfa/enroll/start/"),
    "Failed to start two-factor enrollment",
  );
};
export const confirm_mfa_enrollment = async (body: MfaEnrollConfirmForm): Promise<MfaStatus> => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.POST("/api/clank/user/mfa/enroll/confirm/", { body }),
    "Failed to enable two-factor authentication",
  );
};
export const disable_mfa = async (body: MfaTotpCodeForm): Promise<MfaStatus> => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.POST("/api/clank/user/mfa/disable/", { body }),
    "Failed to disable two-factor authentication",
  );
};
export const get_recording_os_permissions = async () =>
  expectData(
    await coreClient.GET("/api/recording/os-permissions/"),
    "Failed to load recording permissions",
  );
export const request_recording_os_permission = async (permission: RecordingOsPermissionKind) =>
  expectData(
    await coreClient.POST("/api/recording/os-permissions/{permission}/request/", {
      params: {
        path: {
          permission,
        },
      },
    }),
    "Failed to request recording permission",
  );
export const open_recording_os_permission_settings = async (
  permission: RecordingOsPermissionKind,
) =>
  expectData(
    await coreClient.POST("/api/recording/os-permissions/{permission}/open-settings/", {
      params: {
        path: {
          permission,
        },
      },
    }),
    "Failed to open recording permission settings",
  );

export const fetch_screenshot = async (
  recordingId: string,
  screenshotFile: string,
): Promise<string> => {
  const companyClient = createCompanyRouterClient();
  const download = expectData(
    await companyClient.GET("/api/clank/recordings/{recording_id}/screenshot/{screenshot_file}", {
      params: {
        path: {
          recording_id: recordingId,
          screenshot_file: screenshotFile,
        },
      },
    }),
    "Failed to fetch screenshot URL",
  );
  return download.url;
};

/**
 * Start an analysis run. With no `startStage` this is a full run from scratch;
 * resuming mid-pipeline has to name the run whose earlier stages it reuses,
 * since artifacts belong to a run rather than to the recording.
 */
export const analyze_recording = async (
  recordingId: string,
  startStage?: AnalyzerStage,
  baseRunId?: string,
  disableTools?: boolean,
) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.POST("/api/clank/recordings/{recording_id}/analyze/", {
      params: {
        path: {
          recording_id: recordingId,
        },
      },
      body: {
        ...(startStage && { start_stage: startStage }),
        ...(startStage && baseRunId && { base_run_id: baseRunId }),
        ...(disableTools && { disable_tools: true }),
      },
    }),
    "Failed to start analysis",
  );
};

export const cancel_recording_analysis = async (recordingId: string) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.DELETE("/api/clank/recordings/{recording_id}/analyze/", {
      params: {
        path: {
          recording_id: recordingId,
        },
      },
    }),
    "Failed to stop analysis",
  );
};

export const update_recording_name = async (recordingId: string, name: string | null) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.PATCH("/api/clank/recordings/{recording_id}/", {
      params: {
        path: {
          recording_id: recordingId,
        },
      },
      body: {
        name: name,
      },
    }),
    "Failed to rename recording",
  );
};

export const set_recording_inclusion = async (recordingId: string, include: boolean) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.PATCH("/api/clank/recordings/{recording_id}/", {
      params: {
        path: {
          recording_id: recordingId,
        },
      },
      body: {
        include_in_processes: include,
      },
    }),
    "Failed to update recording",
  );
};

export const get_recording_feed_ws_url = () => getWsUrl("/ws/recording/feed/");

export const check_for_updates = async () =>
  expectData(await coreClient.POST("/api/update/check/"), "Failed to check for updates");
export const apply_update = async () =>
  expectData(await coreClient.POST("/api/update/apply/"), "Failed to apply update");

export type ForgejoReleaseAsset = {
  name: string;
  size: number;
  browser_download_url: string;
};

export type ForgejoRelease = {
  name?: string | null;
  tag_name: string;
  created_at?: string | null;
  assets?: ForgejoReleaseAsset[] | null;
};

export const get_clank_releases = async (): Promise<ForgejoRelease[]> => {
  if (IS_STATIC_DEMO_FRONTEND) {
    return [];
  }

  const baseUrl = get_company_api_url();
  const response = await fetch(
    `${baseUrl}/api/forgejo/repos/platonic-research/clank-core/releases/`,
    { credentials: "include" },
  );
  if (!response.ok) {
    throw new Error("Failed to load Platonic releases");
  }
  return (await response.json()) as ForgejoRelease[];
};

export const get_clank_release_asset_download_url = (assetUrl: string): string => {
  const baseUrl = get_company_api_url();
  return `${baseUrl}/api/forgejo/download/?url=${encodeURIComponent(assetUrl)}`;
};

export const get_my_permissions = async () => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/permissions/"),
    "Failed to load permissions",
  );
};

export const create_user = async (body: UserForm) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.POST("/api/clank/user/", {
      body: {
        username: body.username,
        password: body.password,
        company_id: body.company_id,
        ...(body.email != null ? { email: body.email } : {}),
        ...(body.first_name != null ? { first_name: body.first_name } : {}),
        ...(body.last_name != null ? { last_name: body.last_name } : {}),
        ...(body.seeded ? { seeded: true } : {}),
      },
    }),
    "Failed to create user",
  );
};

export const enroll_user = async (body: UserEnrollEmailForm) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.POST("/api/clank/user/enroll/", {
      body: body,
    }),
    "Failed to enroll user",
  );
};

export const create_company = async (body: CreateCompanyForm) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.POST("/api/clank/company/", {
      body,
    }),
    "Failed to create company",
  );
};

export const update_company = async (companyId: number, body: CompanyUpdateForm) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.PUT("/api/clank/company/{company_id}/", {
      params: {
        path: {
          company_id: companyId,
        },
      },
      body,
    }),
    "Failed to update company",
  );
};

export const update_user = async (userId: string, body: UserUpdateForm) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.PUT("/api/clank/user/{user_id}/", {
      params: {
        path: {
          user_id: userId,
        },
      },
      body,
    }),
    "Failed to update user",
  );
};

export const get_user_permissions = async (userId: string) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.GET("/api/clank/user/{user_id}/permissions/", {
      params: {
        path: {
          user_id: userId,
        },
      },
    }),
    "Failed to load user permissions",
  );
};

export const set_user_permissions = async (userId: string, body: ClankPermissions) => {
  const companyClient = createCompanyRouterClient();
  return expectData(
    await companyClient.PUT("/api/clank/user/{user_id}/permissions/", {
      params: {
        path: {
          user_id: userId,
        },
      },
      body,
    }),
    "Failed to update user permissions",
  );
};

export const get_users = async () =>
  get_all_paginated_pages<User>(async page => {
    const companyClient = createCompanyRouterClient();
    const response = await companyClient.GET("/api/clank/sync/{model}/", {
      params: {
        query: {
          page,
        },
        path: {
          model: "user",
        },
      },
    });
    const data = expectData(response, "Failed to fetch users");
    type _Check = Assert<PaginatedResponse<User> extends typeof data ? true : false>;
    return data as PaginatedResponse<User>;
  });

export const get_companies = async () =>
  get_all_paginated_pages<Company>(async page => {
    const companyClient = createCompanyRouterClient();
    const response = await companyClient.GET("/api/clank/sync/{model}/", {
      params: {
        query: {
          page,
        },
        path: {
          model: "company",
        },
      },
    });
    const data = expectData(response, "Failed to fetch companies");
    type _Check = Assert<PaginatedResponse<Company> extends typeof data ? true : false>;
    return data as PaginatedResponse<Company>;
  });
